import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { LinkLabResultRequest } from '@/types/scheduling-api';

/**
 * POST /api/Patients/ReceivedLabResults
 *
 * Links an uploaded patient document to one or more lab orders. Mirrors
 * Flutter `linkLabResult` (`scheduling_data_source_impl.dart:1195-1212`).
 * On success, the affected orders move to `Result received`.
 *
 * Wire shape:
 *   - Body: `{ labRequestIds: string[], patientDocumentId: string }`
 *   - 2xx empty body — typed as `void` (Flutter `Success("")`)
 *
 * Flutter usage:
 *   1. Upload document via `AddUpdatePatientDocumentEvent`
 *   2. On `AddUpdatePatientDocumentResponseState` (carries the new
 *      document id), dispatch `LinkLabResultsEvent` with that id +
 *      the selected lab-order ids.
 *
 * The two-step is enforced by the consumer; this hook only owns the
 * link step. The document-upload step is handled separately (see
 * patient-document-upload flow if/when that ships in PM).
 *
 * Cache invalidation: on success, invalidate `['lab-orders', ...]` so
 * the affected rows refresh with their new status + documentUrl.
 *
 * Wire VERIFIED against the 2026-05-21 dev curl (Andrew curl-5):
 *   body: `{"labRequestIds":["<labId>"],"patientDocumentId":"<docId>"}`
 *   response: `true` (boolean, not empty)
 * The hook types it as `void` and discards the response — caller
 * cares only about success vs failure (cache invalidation handles
 * the rest).
 *
 * SECURITY — role posture (PR #295 §3 ESCALATE → Andrew direction
 * 2026-05-21):
 *   Same posture as `useUpdateLabOrderStatus` — backend does NOT
 *   enforce a Phlebotomist gate, accepted by Andrew on the rationale
 *   that the upload chain (which precedes this call) is intentionally
 *   cross-role. Defence is via React tab gate + feature flag +
 *   in-dialog PHI safeguards (file picker → server-issued doc id →
 *   review step). Not a security boundary against a determined
 *   authenticated caller. See `useUpdateLabOrderStatus` for the full
 *   write-up.
 */

interface LinkLabResultVariables {
  request: LinkLabResultRequest;
}

export function useLinkLabResult(): UseMutationResult<
  void,
  Error,
  LinkLabResultVariables
> {
  const qc = useQueryClient();
  return useMutation<void, Error, LinkLabResultVariables>({
    mutationFn: ({ request }) =>
      post<void>('/api/Patients/ReceivedLabResults', request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lab-orders'] });
    },
  });
}
